import { useMutation } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import { type NsfwPresetLevel, type AccuracyLevel, DEFAULT_BLUEJAY_CONFIG } from "@/config/nsfwRulePresets";

export interface BlueJayRules {
  categories: string[];
  allowlist: string[];
  blocklist: string[];
  config: {
    nsfwLevel: NsfwPresetLevel;
    blockVideos: boolean;
    human: boolean;
    accuracy: AccuracyLevel;
  };
}

export function useDeviceBrowserPolicies(deviceId: string) {
  const { toast } = useToast();
  
  // Mutation to update device policies - only uses blueJayRules field
  const updateDevicePoliciesMutation = useMutation(api.devices.policies.update);
  
  // Update browser policy - ONLY sends blueJayRules field
  // Accepts onSuccess callback to refresh the calling component's data
  const updateBrowserPolicy = async (
    updates: {
      categories?: string[];
      allowlist?: string[];
      blocklist?: string[];
      nsfwLevel?: NsfwPresetLevel;
      blockVideos?: boolean;
      human?: boolean;
      accuracy?: AccuracyLevel;
    },
    currentValues: {
      categories?: string[];
      allowlist?: string[];
      blocklist?: string[];
      nsfwLevel: NsfwPresetLevel;
      blockVideos?: boolean;
      human: boolean;
      accuracy: AccuracyLevel;
    },
    onSuccess?: () => void
  ) => {
    if (!deviceId) {
      toast({
        title: "Error",
        description: "Device ID not available",
        variant: "destructive",
      });
      return;
    }

    try {
      // Merge updates with current values to preserve all settings
      const blueJayRules: BlueJayRules = {
        categories: updates.categories ?? currentValues.categories ?? [],
        allowlist: updates.allowlist ?? currentValues.allowlist ?? [],
        blocklist: updates.blocklist ?? currentValues.blocklist ?? [],
        config: {
          nsfwLevel: updates.nsfwLevel ?? currentValues.nsfwLevel ?? DEFAULT_BLUEJAY_CONFIG.nsfwLevel,
          blockVideos: updates.blockVideos ?? currentValues.blockVideos ?? false,
          human: updates.human ?? currentValues.human ?? DEFAULT_BLUEJAY_CONFIG.human,
          accuracy: updates.accuracy ?? currentValues.accuracy ?? DEFAULT_BLUEJAY_CONFIG.accuracy,
        },
      };

      console.log("[useDeviceBrowserPolicies] Updating with blueJayRules:", blueJayRules);

      // ONLY send blueJayRules field - nothing else
      await updateDevicePoliciesMutation({
        device: deviceId as any,
        blueJayRules,
      });

      // Call the onSuccess callback to refresh the caller's data
      if (onSuccess) {
        onSuccess();
      }

      toast({
        title: "Success",
        description: "Browser policy updated successfully",
      });
    } catch (error) {
      console.error("Failed to update browser policy:", error);
      toast({
        title: "Error",
        description: "Failed to update browser policy",
        variant: "destructive",
      });
    }
  };

  // Set browser to whitelist mode: block all categories, keep allowlist
  const setWhitelistMode = async (
    allCategoryNames: string[], 
    currentConfig?: { nsfwLevel?: NsfwPresetLevel; blockVideos?: boolean; human?: boolean; accuracy?: AccuracyLevel },
    currentAllowlist?: string[],
    onSuccess?: () => void
  ) => {
    if (!deviceId) {
      toast({
        title: "Error",
        description: "Device ID not available",
        variant: "destructive",
      });
      return;
    }

    try {
      const blueJayRules: BlueJayRules = {
        categories: allCategoryNames, // Block all categories
        allowlist: currentAllowlist ?? [], // Preserve existing allowlist
        blocklist: [], // Reset blocklist in whitelist mode
        config: {
          nsfwLevel: currentConfig?.nsfwLevel ?? DEFAULT_BLUEJAY_CONFIG.nsfwLevel,
          blockVideos: currentConfig?.blockVideos ?? false,
          human: currentConfig?.human ?? DEFAULT_BLUEJAY_CONFIG.human,
          accuracy: currentConfig?.accuracy ?? DEFAULT_BLUEJAY_CONFIG.accuracy,
        },
      };

      console.log("[useDeviceBrowserPolicies] Setting whitelist mode with:", blueJayRules);

      // ONLY send blueJayRules field
      await updateDevicePoliciesMutation({
        device: deviceId as any,
        blueJayRules,
      });

      if (onSuccess) {
        onSuccess();
      }

      toast({
        title: "Whitelist Mode Enabled",
        description: "All categories blocked. Only whitelisted sites will be accessible.",
      });
    } catch (error) {
      console.error("Failed to set whitelist mode:", error);
      toast({
        title: "Error",
        description: "Failed to update browser settings",
        variant: "destructive",
      });
    }
  };

  // Default blocked categories when switching to filtered mode
  const DEFAULT_BLOCKED_CATEGORIES = [
    "Women's Swimwear & Intimates",
    "Women's Personal Apparel",
    "Gambling",
    "Dating",
    "Violence",
    "Defacement",
    "Drugs/Alcohol",
    "Piracy",
  ];

  // Set browser to filtered mode: block specific categories by default
  const setFilteredMode = async (
    currentConfig?: { nsfwLevel?: NsfwPresetLevel; blockVideos?: boolean; human?: boolean; accuracy?: AccuracyLevel },
    currentLists?: { allowlist?: string[]; blocklist?: string[]; categories?: string[] },
    onSuccess?: () => void
  ) => {
    if (!deviceId) {
      toast({
        title: "Error",
        description: "Device ID not available",
        variant: "destructive",
      });
      return;
    }

    try {
      const blueJayRules: BlueJayRules = {
        categories: DEFAULT_BLOCKED_CATEGORIES, // Block these categories by default
        allowlist: currentLists?.allowlist ?? [],
        blocklist: currentLists?.blocklist ?? [],
        config: {
          nsfwLevel: currentConfig?.nsfwLevel ?? DEFAULT_BLUEJAY_CONFIG.nsfwLevel,
          blockVideos: currentConfig?.blockVideos ?? false,
          human: currentConfig?.human ?? DEFAULT_BLUEJAY_CONFIG.human,
          accuracy: currentConfig?.accuracy ?? DEFAULT_BLUEJAY_CONFIG.accuracy,
        },
      };

      console.log("[useDeviceBrowserPolicies] Setting filtered mode with:", blueJayRules);

      // ONLY send blueJayRules field
      await updateDevicePoliciesMutation({
        device: deviceId as any,
        blueJayRules,
      });

      if (onSuccess) {
        onSuccess();
      }

      toast({
        title: "Filtered Mode Enabled",
        description: "Browser access enabled with category filtering.",
      });
    } catch (error) {
      console.error("Failed to set filtered mode:", error);
      toast({
        title: "Error",
        description: "Failed to update browser settings",
        variant: "destructive",
      });
    }
  };

  // Disable browser completely: blueJayRules = false
  const disableBrowser = async (onSuccess?: () => void) => {
    if (!deviceId) {
      toast({
        title: "Error",
        description: "Device ID not available",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log("[useDeviceBrowserPolicies] Disabling browser");

      await updateDevicePoliciesMutation({
        device: deviceId as any,
        blueJayRules: false as any,
      });

      if (onSuccess) {
        onSuccess();
      }

      toast({
        title: "Browser Disabled",
        description: "Browser access has been disabled for this device.",
      });
    } catch (error) {
      console.error("Failed to disable browser:", error);
      toast({
        title: "Error",
        description: "Failed to update browser settings",
        variant: "destructive",
      });
    }
  };

  // Update categories within blueJayRules (for category table)
  // Requires currentRules to be passed in since we no longer read internally
  const updateCategories = async (
    newCategories: string[],
    currentRules: BlueJayRules,
    onSuccess?: () => void
  ) => {
    if (!deviceId) {
      toast({
        title: "Error",
        description: "Device ID not available",
        variant: "destructive",
      });
      return;
    }

    try {
      const blueJayRules: BlueJayRules = {
        ...currentRules,
        categories: newCategories,
      };

      console.log("[useDeviceBrowserPolicies] Updating categories:", blueJayRules);

      // ONLY send blueJayRules field
      await updateDevicePoliciesMutation({
        device: deviceId as any,
        blueJayRules,
      });

      if (onSuccess) {
        onSuccess();
      }

      toast({
        title: "Success",
        description: "Category settings updated successfully",
      });
    } catch (error) {
      console.error("Failed to update categories:", error);
      toast({
        title: "Error",
        description: "Failed to update category settings",
        variant: "destructive",
      });
      throw error;
    }
  };

  return {
    isLoading: false,
    updateBrowserPolicy,
    setWhitelistMode,
    setFilteredMode,
    disableBrowser,
    updateCategories,
  };
}
